import { useSearchParams } from 'react-router-dom';
import { useCallback } from 'react';

type SearchParam = {
  key: string;
  value: string[];
};

const useSearchParamActions = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const getSearchParams = useCallback((): SearchParam[] => {
    const params: SearchParam[] = [];
    searchParams.forEach((value, key) => {
      const existingParam = params.find((param) => param.key === key);
      if (existingParam) {
        existingParam.value.push(value);
      } else {
        params.push({ key, value: [value] });
      }
    });
    return params;
  }, [searchParams]);

  const appendSearchParam = useCallback(
    (key: string, value: string, replace: boolean = true) => {
      const newParams = new URLSearchParams(searchParams);

      if (replace) {
        newParams.set(key, value);
      } else {
        newParams.append(key, value);
      }

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const removeSearchParam = useCallback(
    (key: string, value?: string) => {
      const newParams = new URLSearchParams(searchParams);

      if (value !== undefined) {
        const values = newParams.getAll(key);
        newParams.delete(key);
        values.forEach((val) => {
          if (val !== value) {
            newParams.append(key, val);
          }
        });
      } else {
        newParams.delete(key);
      }

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const removeAllSearchParams = useCallback(
    (keys: string[]) => {
      const newParams = new URLSearchParams(searchParams);

      keys.forEach((key) => {
        newParams.delete(key);
      });

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const getSearchParam = useCallback(
    (key: string): string | undefined => {
      return searchParams.get(key) || undefined;
    },
    [searchParams]
  );

  const getAllSearchParams = useCallback(
    (): SearchParam[] => getSearchParams(),
    [getSearchParams]
  );

  return {
    appendSearchParam,
    removeSearchParam,
    removeAllSearchParams,
    getSearchParam,
    getAllSearchParams,
    searchParams,
  };
};

export default useSearchParamActions;
